import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnInit,
    EventEmitter,
    Output
} from '@angular/core';
import {
    ActivatedRoute,
    ActivatedRouteSnapshot,
    NavigationEnd,
    Router,
    RouterLink
} from '@angular/router';
import {
    debounceTime,
    filter,
    map,
    share,
    shareReplay,
    startWith,
    switchMap,
    tap,
    throttleTime
} from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { UiQuery } from '../../../state/ui/ui.query';
import { IconsModule } from '../../../icons/icons.module';
import { AppConfigQuery } from '../../../../core/state/app-config/app-config.query';
import { iif, ObservedValueOf, ReplaySubject } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { filterNilValue } from '@datorama/akita';
import { fadeInOut } from '../../../animations/fade-in-out.animation';
import { getDeepestActivatedRoute } from '../../../../features/data-entry-flow/components/sb-data-entry-flow/sb-data-entry-flow.component';

export type SbBreadcrumbs = Array<SbBreadcrumb>;
export interface SbBreadcrumb {
    label: string;
    path: string;
    queryParams?: any;
    queryParamsHandling?: any;
}

interface BreadcrumbData {
    path?: string;
    breadcrumb?: string;
    [key: string]: any;
}

@Component({
    selector: 'sb-breadcrumbs',
    standalone: true,
    template: `
        <nav
            aria-label="Breadcrumb"
            [class.sticky-breadcrumbs]="
                scrollPosition && (scrollPosition$ | async) > scrollPosition
            "
        >
            <ng-container *ngIf="viewportSize$ | async as viewportSize">
                <ng-container *ngIf="viewportSize !== 'xs'">
                    <ol *ngIf="breadcrumbs$ | async as breadcrumbs">
                        <li *ngFor="let breadcrumb of breadcrumbs; let i = index">
                            <ng-container [ngSwitch]="clickable">
                                <!-- Clickable breadcrumbs -->
                                <a
                                    *ngSwitchCase="true"
                                    [attr.aria-current]="
                                        i === breadcrumbs.length - 1 ? 'page' : null
                                    "
                                    (click)="onBreadcrumbClick(breadcrumb, i)"
                                    [class.disabled]="disabled"
                                    role="button"
                                    style="cursor: pointer"
                                >
                                    {{ breadcrumb.label | translate }}
                                </a>

                                <!-- Regular router-linked breadcrumbs -->
                                <ng-container *ngSwitchDefault>
                                    <a
                                        *ngIf="breadcrumb.label !== 'Documents'"
                                        [attr.aria-current]="
                                            i === breadcrumbs.length - 1 ? 'page' : null
                                        "
                                        [routerLink]="disabled ? null : breadcrumb.path"
                                        [relativeTo]="
                                            useRouterEvents ? activeRoute : undefined
                                        "
                                        [queryParams]="breadcrumb.queryParams || null"
                                        [queryParamsHandling]="
                                            breadcrumb?.queryParamsHandling || null
                                        "
                                    >
                                        {{ breadcrumb.label | translate }}
                                    </a>

                                    <span *ngIf="breadcrumb.label === 'Documents'">
                                        <strong>
                                            {{ breadcrumb.label | translate }}
                                        </strong>
                                    </span>
                                </ng-container>
                            </ng-container>
                        </li>
                    </ol>
                </ng-container>
                <ng-container *ngIf="viewportSize === 'xs'">
                    <ol class="d-block" *ngIf="!(parentBreadcrumb$ | async)">
                        <ng-container *ngIf="breadcrumbs$ | async as breadcrumbs">
                            <li [@fadeInOut]="breadcrumbs" *ngIf="breadcrumbs.length > 1">
                                <a
                                    aria-current="page"
                                    [queryParams]="breadcrumbs[0].queryParams || null"
                                    [queryParamsHandling]="
                                        breadcrumbs[0]?.queryParamsHandling || null
                                    "
                                    [routerLink]="disabled ? null : breadcrumbs[0].path"
                                >
                                    {{ breadcrumbs[0].label | translate }}
                                </a>
                            </li>
                        </ng-container>
                    </ol>
                    <ol
                        [@fadeInOut]="parentBreadcrumb"
                        *ngIf="parentBreadcrumb$ | async as parentBreadcrumb"
                        class="sb-mobile-breadcrumbs"
                    >
                        <li>
                            <tabler-icon
                                class="position-absolute ms-2"
                                name="chevron-left"
                            ></tabler-icon>
                            <a
                                [queryParams]="parentBreadcrumb.queryParams || null"
                                [queryParamsHandling]="
                                    parentBreadcrumb?.queryParamsHandling || null
                                "
                                [routerLink]="disabled ? null : parentBreadcrumb.path"
                                class="d-flex align-items-center ms-3"
                            >
                                {{ parentBreadcrumb.label | translate }}
                            </a>
                        </li>
                        <h3
                            class="h4 mb-0"
                            @fadeInOut
                            *ngIf="
                                !useRouterEvents && breadcrumbs$ | async as breadcrumbs
                            "
                        >
                            {{ breadcrumbs[breadcrumbs.length - 1].label | translate }}
                        </h3>
                    </ol>
                </ng-container>
            </ng-container>
        </nav>
    `,
    animations: [fadeInOut],
    imports: [RouterLink, CommonModule, IconsModule, TranslateModule],
    styles: [
        `
            @use '/src/styles/new/mixins/breakpoints';
            :host {
                // TODO: refactor this when we redo the menu and navigation, below is a temporary fix to make this component work on mobile for sb-allocations
                &.local-breadcrumbs {
                    position: sticky;
                    min-height: 2.75rem;
                    // display: grid;
                    // grid-template-columns: 1fr;
                    // grid-template-rows: 1fr;
                    top: 0;
                    z-index: 1;
                    background: var(--bg-color);
                    /*linear-gradient(
                                       to bottom,
                                       var(--level-0-bg-color) 40%,
                                       color-mix(in srgb, var(--level-0-bg-color) 85%, transparent)
                                   );*/

                    > nav {
                        display: contents;
                    }

                    > nav,
                    > nav > ol {
                        min-height: inherit;
                        // grid-row: 1;
                        // grid-column: 1;
                    }

                    // make breadcrumbs smaller on smaller screens
                    /*.sticky-breadcrumbs {
                        a {
                            font-size: 14px !important;
                            padding: 0.375rem 1rem !important;
                            transition: all ease-in-out 300ms;
                        }
                        h3 {
                            font-size: 14px !important;
                            transition: all ease-in-out 300ms;
                        }
                    }*/
                    @include breakpoints.media-breakpoint-only(xs) {
                        h3,
                        a {
                            font-size: 14px !important;
                        }
                    }
                }
            }
            a.disabled {
                pointer-events: none;
                opacity: 0.6;
            }
        `
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SbBreadcrumbsComponent implements OnInit {
    get disabled(): boolean {
        return this._disabled;
    }

    @Input() set disabled(value: boolean) {
        this._disabled = value;
    }
    get useRouterEvents(): boolean {
        return this._useRouterEvents;
    }

    @Input() set useRouterEvents(value: typeof this._useRouterEvents) {
        this._useRouterEvents = value + '' === 'true';
    }
    @Input() set breadcrumbs(value: ObservedValueOf<typeof this._breadcrumbs$>) {
        this._breadcrumbs$.next(value);
    }

    @Input() clickable = false;

    _useRouterEvents = true;
    _disabled = false;

    @Output() breadcrumbClick = new EventEmitter<{
        breadcrumb: SbBreadcrumb;
        index: number;
    }>();

    constructor(
        public route: ActivatedRoute,
        private _router: Router,
        private _uiQuery: UiQuery,
        private _appConfigQuery: AppConfigQuery
    ) {}
    activeRoute = null; // use relative links when breadcrumbs are generated from router events

    private _breadcrumbs$ = new ReplaySubject<Array<SbBreadcrumb>>(1);

    scrollPosition = 48;
    scrollPosition$ = this._uiQuery.scrollYPositionObservable$.pipe(
        switchMap((observable) => observable),
        throttleTime(100, undefined, { leading: true, trailing: true }),
        shareReplay(1)
    );

    readonly viewportSize$ = this._uiQuery.viewportSize$;
    private readonly _hideRootBreadcrumb = this._appConfigQuery.get().hideRootBreadcrumb; // prevent navigating back to dashboard (only used by NAPS)
    private readonly _initialized$ = new ReplaySubject<boolean>(1); // used to trigger streams when component is initialized

    /** breadcrumbs$
     * @description Observable for breadcrumbs to render. If `useRouterEvents` is set to `true` (default),
     * breadcrumbs will get generated based on the route config tree, if not, it will rely on what's being passed to breadcrumb input
     * i.e. it's handled outside of this component.
     */
    readonly breadcrumbs$ = this._initialized$.pipe(
        switchMap((_) => {
            return iif(
                () => this.useRouterEvents,
                this._router.events.pipe(
                    filter((event) => event instanceof NavigationEnd),
                    debounceTime(0),
                    map((_) => this._getTree(this.route.snapshot, [])),
                    startWith(this._getTree(this.route.snapshot, [])),
                    tap(
                        (breadcrumbs) =>
                            (this.activeRoute = getDeepestActivatedRoute(this.route))
                    ),
                    map((breadcrumbs) => {
                        const isAdmin =
                            this.route.snapshot?.data?.roles?.includes('ADMINISTRATOR');
                        const isDocuments =
                            this.route.snapshot?.data?.breadcrumb === 'Documents';

                        let crumbs = [];

                        const firstBreadcrumbText = isDocuments
                            ? 'Documents'
                            : isAdmin
                            ? 'Manage'
                            : 'Portfolio';

                        crumbs = [
                            { breadcrumb: firstBreadcrumbText },
                            ...breadcrumbs.filter((item) => item.breadcrumb !== 'skip')
                        ];
                        if (isDocuments) crumbs.pop();
                        return crumbs.map(
                            (item, i): SbBreadcrumb => ({
                                label: item.breadcrumb,
                                path:
                                    item.breadcrumb === 'Portfolio'
                                        ? ''
                                        : i === crumbs.length - 1
                                        ? null
                                        : Array(crumbs.length - i - 1)
                                              .fill('../')
                                              .join('')
                                /*i === 0 && !isAdmin
                                          ? '../'
                                          : i !== crumbs.length - 1
                                          ? './'
                                          : null*/
                            })
                        );
                    }),
                    map((breadcrumbs) =>
                        this._hideRootBreadcrumb ? breadcrumbs.slice(1) : breadcrumbs
                    )
                ),
                this._breadcrumbs$.asObservable()
            );
        }),
        share()
    );

    /** parentBreadcrumb$
     * @description Observable for the parent breadcrumb at the root.
     */
    parentBreadcrumb$ = this.breadcrumbs$.pipe(
        filterNilValue(),
        map((breadcrumbs) =>
            breadcrumbs.length > 1 ? breadcrumbs[breadcrumbs.length - 2] : null
        )
    );

    private _getTree(activatedRoute: ActivatedRouteSnapshot, state: BreadcrumbData[]) {
        const data: BreadcrumbData = {
            ...activatedRoute.data,
            path: activatedRoute.routeConfig?.path
        };

        const lastBreadcrumb = state[state.length - 1];
        if (lastBreadcrumb?.breadcrumb === data.breadcrumb) {
            if (activatedRoute.firstChild) {
                return this._getTree(activatedRoute.firstChild, state);
            }
            return state;
        }

        state = [...state, data];
        if (activatedRoute.firstChild) {
            return this._getTree(activatedRoute.firstChild, state);
        }
        return state;
    }

    ngOnInit() {
        this._initialized$.next(true);
    }

    onBreadcrumbClick(breadcrumb: SbBreadcrumb, index: number) {
        if (!this.disabled) {
            this.breadcrumbClick.emit({ breadcrumb, index });
        }
    }
}
